/**
 * Shell — per docs/specs/README.md
 * Core grid layout. Mounts the sidebar, topbar, detail panel, plus the global
 * command palette and keyboard shortcut layer.
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v2
 */
import type { ReactNode } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { useCallback, useRef, useState } from 'react';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { CommandPalette } from '../components/command-palette';
import { ShortcutHelp } from '../components/shortcut-help';
import { useKeyboardShortcuts } from '../hooks/use-keyboard-shortcuts';

interface ShellContext {
    detailPanel: ReactNode | null;
    setDetailPanel: (node: ReactNode | null) => void;
    openCommandPalette: () => void;
}

export function useShell() {
    return useOutletContext<ShellContext>();
}

export function Shell() {
    const [detailPanel, setDetailPanel] = useState<ReactNode | null>(null);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    // Latest-state refs so the keyboard hook sees fresh values each event.
    const paletteOpenRef = useRef(paletteOpen);
    paletteOpenRef.current = paletteOpen;
    const helpOpenRef = useRef(helpOpen);
    helpOpenRef.current = helpOpen;

    const openCommandPalette = useCallback(() => {
        setHelpOpen(false);
        setPaletteOpen(true);
    }, []);
    const closeCommandPalette = useCallback(() => setPaletteOpen(false), []);
    const openHelp = useCallback(() => {
        setPaletteOpen(false);
        setHelpOpen(true);
    }, []);
    const closeHelp = useCallback(() => setHelpOpen(false), []);

    useKeyboardShortcuts({
        onTogglePalette: openCommandPalette,
        onClosePalette: closeCommandPalette,
        onOpenHelp: openHelp,
        isPaletteOpen: () => paletteOpenRef.current,
        isHelpOpen: () => helpOpenRef.current
    });

    return (
        <div className="admin-shell" data-panel={detailPanel ? 'open' : 'closed'}>
            <Sidebar />
            <Topbar onOpenPalette={openCommandPalette} />
            <main className="admin-main">
                <Outlet
                    context={
                        {
                            detailPanel,
                            setDetailPanel,
                            openCommandPalette
                        } satisfies ShellContext
                    }
                />
            </main>
            {detailPanel && <aside className="admin-detail-panel">{detailPanel}</aside>}
            <CommandPalette open={paletteOpen} onClose={closeCommandPalette} />
            <ShortcutHelp open={helpOpen} onClose={closeHelp} />
        </div>
    );
}
